<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
        :showBtn="true"
        title="Supershop POS Invoice"
        @onClickCloseButton="goToList"
      />
    </div>

    <div class="row p-2">
      <div class="col-12">
        <TableHeader
          :formData="formData"
          :tableItems="tableItems"
          :salesAccountHeads="salesAccountHeads"
          :additionalProductQuery="additionalProductQuery"
          @finalize="handleSubmit"
          ref="formHeader"
        />
      </div>
    </div>

    <div class="col-12 px-2 mt-2">
      <ListTable
        :tableItems="tableItems"
        :vatRate="vatRate"
      />
    </div>

    <div class="col-12 px-2 pb-2">
      <PosBillingSummary
        :tableItems="tableItems"
        :salesAccountHeads="salesAccountHeads"
        @clear="resetForm"
        @newBill="newBill"
        @print="handleSubmit"
        ref="billingSummary"
      />
    </div>
    
    <Loader v-if="loader"/>

    <PaymentModal
      v-if="isPaymentModal"
      :grandTotal="parseFloat(totals.grandTotalVal)"
      :rewardPointsAccountHeads="rewardPointsAccountHeads"
      :cashAndBank="cashAndBank"
      @close="isPaymentModal = false"
      @onSubmit="submitData"
    />

    <div ref="posReceipt">
      <teleport to="body">
        <div id="pos-print-area-portal">
          <PosReceiptPrint
              :company="company"
              :invoice="invoiceRes"
              :posPrintSize="posPrintSize"
          />
        </div>
      </teleport>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, provide, ref } from 'vue';
import TitleButton from '@/components/atom/TitleButtonSuperShop';
import ListTable from '@/components/atom/super-shop/ListTable.vue';
import TableHeader from '@/components/atom/super-shop/TableHeader.vue';
import PosBillingSummary from '@/components/atom/super-shop/PosBillingSummary.vue';
import PaymentModal from "@/components/molecule/company/pos-invoice/PaymentModal.vue";
import PosReceiptPrint from "@/components/molecule/company/pos-invoice/PosReceiptPrint.vue";
import { useRoute, useRouter } from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import { vatRate } from '@/data/inventory.js';
import handleCompany from "@/services/modules/company";
import pdfPrinter from '@/services/utils/pdfPrinter';
import useDate from "@/services/utils/day";
import Loader from '@/components/atom/LoaderComponent';
import figureFormatter from "@/services/utils/figureFormatter";
import handleBarcode from "@/services/modules/barcode";
import hospitalPosPdfPrinter from '@/services/utils/hospitalPosPdfPrinter';
import handleDropdowns from '@/services/modules/dropdowns';
import { useStore } from "vuex";

const { exportToPDF } = pdfPrinter();
const { fetchSalesBill, ...rest } = handlePurchase();
const { fetchCompanyInfo, fetchActiveCustomizationField, getCompanyPreferences } = handleCompany();
const { formatDate } = figureFormatter();
const { fetchBarcode } = handleBarcode();
const { exportToPDF: exportToPosPDF } = hospitalPosPdfPrinter();
const { fetchDropdownList } = handleDropdowns();

const $route = useRoute();
const $router = useRouter();
const store = useStore();
const date = useDate();

const companyId = $route.params.companyId;
const loader = ref(true);
const submitLoading = ref(false);
const formHeader = ref(null);
const salesAccountHeads = ref([]);
const totals = ref({});
const billingSummary = ref(null);
const invoiceRes = ref({});
const company = ref({});
const barcodeQuery = ref('');
const barcode = ref('');
const qrcode = ref('');
const customizationPdfFields = ref([]);
const cashAndBank = ref([]);
const rewardPointsAccountHeads = ref([]);
const isPaymentModal = ref(false);
const posPrintSize = ref(58);
const posReceipt = ref(null);
let tableItems = ref([]);
let formData = ref({
  company_id: companyId,
  contact_profile_id: null,
  referrer_id: null,
  account_head_id: null,
  ipd_register_id: null,
  vat_payable_account_head_id: null,
  project_id: null,
  cost_centre_id: null,
  location_id: null,
  mop_account_head_id: null,
  discount_head_id: null,
  service_resource_id: null,
  receipt_reference: null,
  adjustment_amount: null,
  receipt_description: "Pos bill payment",
  payment: null,
  status: 'active',
  date: '',
  has_item_detail: true,
  description: '',
  order_number: null,
  sales_person_id: null,
  commission_percent: null,
  lc_number: null,
  item_details: [],
  points_redeemed: 0
});
provide('formData', formData);
provide('totals', totals);
const showSuccess =  inject('showSuccess');
const showError =  inject('showError');

const additionalProductQuery = computed(() => {
  return {
    location_id: formData.value.location_id,
    with_desc_stock: 1
  }
})

onMounted(() => {
  loader.value=true
  const dropdownRes = fetchDropdownList(getDropDownQuery());

  Promise.all([
    dropdownRes.then(res => {
      if(! res.status) return;

      if(res.data.account_head_list_by_slag.accounts_receivable) {
        formData.value.account_head_id = res.data.account_head_list_by_slag.accounts_receivable[0].id;
      }

      if(res.data.account_head_list_by_slag.vat_payable) {
        formData.value.vat_payable_account_head_id = res.data.account_head_list_by_slag.vat_payable[0].id;
      }

      if(res.data.account_head_list_by_slag.discount_account) {
        salesAccountHeads.value = res.data.account_head_list_by_slag.discount_account;
      }

      if(res.data.account_head_list_by_slag.cash_and_banks) {
        cashAndBank.value = res.data.account_head_list_by_slag.cash_and_banks;
        formData.value.mop_account_head_id = cashAndBank.value[0].id;
      }

      if(res.data.account_head_list_by_slag.reward_points) {
        rewardPointsAccountHeads.value = res.data.account_head_list_by_slag.reward_points;
      }
    }),
    fetchCompanyInfo(companyId).then(res=> {
      if(res.data) company.value = res.data;
    }),
    getActiveFiledCustomization(),
    getPosPrintSizeData()
  ]).finally(() => {loader.value=false});

  window.addEventListener('keyup', handleGlobalKeyup);
})

onUnmounted(() => {
  window.removeEventListener('keyup', handleGlobalKeyup);
});

const handleGlobalKeyup = (event) => {
  if (event.key === 'F2' || (event.key === 'Enter' && event.ctrlKey)) {
    event.preventDefault();
    handleSubmit();
  }
}

const getPosPrintSizeData = async () => {
  const queryObject = {
    company_id: companyId,
    fields: 'pos_print_size'
  }
  const query = '?' + new URLSearchParams(queryObject).toString();

  await getCompanyPreferences(query)
      .then(res => {
        if (res.status){
          const data = res.data;
          posPrintSize.value = getInitialValue(data, 'pos_print_size')
        }
      });
}

const getInitialValue = (data, field) => {
  const item = data.find((item => item.field === field))
  return item ? item.value : null;
}

const getDropDownQuery = () => {
  let query = `?company_id=${companyId}
    &is_account_heads=1
    &slags[]=discount_account
    &slags[]=reward_points
    &slags[]=vat_payable
    &slags[]=accounts_receivable
    &slags[]=cash_and_banks`;
                
  return query;
}

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${companyId}&customization_type=pdf&type=supershop_pos_pdf`;
  const res = await fetchActiveCustomizationField(companyQuery);
  customizationPdfFields.value = res.data;
};

const goToList = () => {
  $router.push({name: 'supershop-pos-list',
    params: {
      companyId: $route.params.companyId,
      moduleId: $route.params.moduleId,
      menuId: $route.params.menuId,
      pageId: $route.params.pageId
    },
    query: {
      start: $route.query.start,
      end: $route.query.end
    }
  })
}

const resetForm = () => {
  const accountHeadId = formData.value.account_head_id
  const vatPayableAccountHeadId = formData.value.vat_payable_account_head_id
  const mopAccountHeadId = formData.value.mop_account_head_id;
  formData.value = {
    company_id: companyId,
    contact_profile_id: null,
    referrer_id: null,
    ipd_register_id: null,
    project_id: null,
    account_head_id: accountHeadId,
    cost_centre_id: null,
    location_id: null,
    mop_account_head_id: mopAccountHeadId,
    vat_payable_account_head_id: vatPayableAccountHeadId,
    discount_head_id: null,
    service_resource_id: null,
    receipt_reference: null,
    adjustment_amount: null,
    receipt_description: "Pos bill payment",
    payment: null,
    status: 'active',
    date: '',
    has_item_detail: true,
    description: '',
    order_number: null,
    sales_person_id: null,
    commission_percent: null,
    lc_number: null,
    item_details: [],
    points_redeemed: 0
  };
  tableItems.value = [];
  totals.value = {
    subTotalVal: 0,
    totalVatVal: 0,
    totalVal: 0,
    grandTotalVal: 0
  };
  formHeader.value.resetData();
  if (billingSummary.value) {
    billingSummary.value.resetData();
  }
}

const newBill = () => {
  const newTabUrl = $router.resolve({
    name: 'supershop-pos',
    params: {
      companyId: $route.params.companyId,
      moduleId: $route.params.moduleId,
      menuId: $route.params.menuId,
      pageId: $route.params.pageId
    },
    query: {
      start: $route.query.start,
      end: $route.query.end
    }
  }).href;

  window.open(newTabUrl, '_blank');
}

const handleSubmit = () => {
  if(! formData.value.contact_profile_id) {
    return showError('Please select customer');
  }
  if(! tableItems.value.length) {
    return showError('Please add at least one product');
  }
  
  isPaymentModal.value = true;
}

const submitData = () => {
  submitLoading.value = true;

  if(!formData.value.date) formData.value.date = date.currentDate();
  let copyFormData = Object.assign({}, formData.value);
  copyFormData.item_details = tableItems.value;
  copyFormData.sale_type = "pos_invoice"
  copyFormData.receipt_type = "pos_invoice"

  rest.storeBillPayment(copyFormData)
    .then(res => {
      if(! res.status) {
        showError(res.message);
        submitLoading.value = false;
        return;
      }
      submitLoading.value = false;
      isPaymentModal.value = false;
      tableItems.value = [];
      resetForm();
      showSuccess(res.message)
      goToPrint(res.data.id)
    })
    .catch(err=> {
      console.log(err)
      showError('Something went wrong')
      submitLoading.value = false;
  })
}

const goToPrint = async (id) => {
  const user = store.state.user.user
  const companyQuery = `?company_id=${companyId}`;
  const posPrint = customizationPdfFields.value && customizationPdfFields.value.heading_left_pos_print;
  
  try {
    const res = await fetchSalesBill(id, companyQuery);
    if (!res) return;

    invoiceRes.value = res.data;
    let qrcodeData = JSON.stringify({
      ref_no: invoiceRes.value.bill_number,
      ref_date: invoiceRes.value.date,
      created_by: user.name,
      created_date_time: formatDate(new Date())
    })
    barcodeQuery.value = `?code=${invoiceRes.value.bill_number}&qrcode=${qrcodeData}`;

    const barcodeRes = await fetchBarcode(barcodeQuery.value);
    barcode.value = barcodeRes.barcode;
    qrcode.value = barcodeRes.qrcode;

    if( posPrint ) {
      window.print();
      return;
    }
    exportToPDF(company.value, invoiceRes.value, barcode.value, qrcode.value, user.name, 1)
  } catch (e) {
    showError("Something went wrong during printing");
  }
}

</script>
<style scoped>
address{
  margin-bottom: 0 !important;
}
.template-selection {
  margin-left: -97%;
  width: 166%;
}
</style>
